import { HttpClient } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { DialogComponent } from 'src/app/components/dialog/dialog.component';
import { ListaDadosAcaoLinha, ListaDadosColuna } from 'src/app/components/lista-dados/lista-dados.component';
import { BePontoEletronicoAgendamento } from 'src/app/models/BePontoEletronicoAgendamento';
import { DialogData } from 'src/app/models/dialog-data';
import { SnackBarService } from 'src/app/services/snack-bar.service';

@Component({
  selector: 'app-ponto-eletronico-agendamentos',
  templateUrl: './ponto-eletronico-agendamentos.component.html',
  styleUrls: ['./ponto-eletronico-agendamentos.component.css']
})
export class PontoEletronicoAgendamentosComponent implements OnInit, OnDestroy {
  private static readonly DiasUteis = 62;
  private static readonly HorariosPadrao = ['08:00', '12:00', '13:12', '18:00'];

  public agendamentos: BePontoEletronicoAgendamento[] = [];
  public subscription?: Subscription;

  public acoesLinha: ListaDadosAcaoLinha[] = [
    {
      chave: 'ativar',
      texto: 'Ativar',
      icone: 'play_circle',
      visivel: row => !!row && !row.blAtivo
    },
    {
      chave: 'desativar',
      texto: 'Desativar',
      icone: 'pause_circle',
      visivel: row => !!row?.blAtivo
    }
  ];

  public colunasListaDados: ListaDadosColuna[] = [
    {
      campo: 'hrPonto',
      rotulo: 'Horário',
      tamanho: '50px',
      editavel: true,
      edicao: {
        tipoEditor: 'texto',
        validacao: {
          obrigatorio: true,
          regex: '^(?:[01]\\d|2[0-3]):[0-5]\\d$'
        }
      }
    },
    {
      campo: 'blSimular',
      rotulo: 'Tipo',
      tamanho: '120px',
      editavel: true,
      edicao: {
        tipoEditor: 'combobox',
        validacao: { obrigatorio: true },
        opcoes: [
          { valor: false, rotulo: 'Registrar ponto' },
          { valor: true, rotulo: 'Somente simular' }
        ]
      }
    },
    {
      campo: 'blAtivo',
      rotulo: 'Ativo',
      tamanho: '30px',
      editavel: false,
      tipoExibicao: 'status'
    }
  ];

  public constructor(
    private httpClient: HttpClient,
    private dialog: MatDialog,
    private snackBarService: SnackBarService,
  ) {}

  public ngOnInit(): void {
    this.carregarAgendamentos();
  }

  public ngOnDestroy(): void {
    this.unsubscribe();
  }

  public onIniciarEdicao(agendamento: BePontoEletronicoAgendamento): void {
    if (agendamento.id)
      return;

    agendamento.hrPonto = this.obterProximoHorarioPadrao(agendamento);
    agendamento.nrDiasSemana = PontoEletronicoAgendamentosComponent.DiasUteis;
    agendamento.dsDiasSemana = 'Segunda a sexta';
    agendamento.blSimular = false;
    agendamento.blAtivo = true;
  }

  public onSalvarLinha(agendamento: BePontoEletronicoAgendamento): void {
    const horario = agendamento.hrPonto.substring(0, 5);
    const horarioDuplicado = this.agendamentos.some(item =>
      item !== agendamento &&
      (item.hrPonto ?? '').substring(0, 5) === horario);

    if (horarioDuplicado) {
      this.snackBarService.exibirErro(`Já existe um agendamento cadastrado para o horário ${horario}.`);
      return;
    }

    this.unsubscribe();
    const payload: BePontoEletronicoAgendamento = {
      id: agendamento.id ?? null,
      hrPonto: `${agendamento.hrPonto.substring(0, 5)}:00`,
      nrDiasSemana: PontoEletronicoAgendamentosComponent.DiasUteis,
      blSimular: agendamento.blSimular,
      blAtivo: agendamento.blAtivo,
    };

    const requisicao = payload.id
      ? this.httpClient.put<BePontoEletronicoAgendamento>('/api/PontoEletronicoAgendamentos', payload)
      : this.httpClient.post<BePontoEletronicoAgendamento>('/api/PontoEletronicoAgendamentos', payload);

    this.subscription = requisicao.subscribe({
      next: (res) => {
        Object.assign(agendamento, this.prepararParaTela(res));
        this.agendamentos = [...this.agendamentos]
          .sort((a, b) => a.hrPonto.localeCompare(b.hrPonto));
        this.unsubscribe();
        this.snackBarService.exibirSucesso(payload.id ? 'Agendamento salvo!' : 'Agendamento criado!');
      },
      error: (err) => {
        this.unsubscribe();
        this.snackBarService.exibirErro(this.obterMensagemErro(
          err,
          'Não foi possível salvar o agendamento. Tente novamente.'));
      }
    });
  }

  public executarAcaoLinha(evento: { chave: string; linha: BePontoEletronicoAgendamento }): void {
    if (evento.chave === 'ativar')
      this.confirmarAlteracaoAtivo(evento.linha, true);

    if (evento.chave === 'desativar')
      this.confirmarAlteracaoAtivo(evento.linha, false);
  }

  public onExcluir(agendamento: BePontoEletronicoAgendamento): void {
    if (!agendamento.id || this.subscription)
      return;

    const data: DialogData = {
      title: 'Excluir agendamento',
      message: `Deseja excluir definitivamente da sua lista o ponto das ${agendamento.hrPonto}? O histórico de execuções será preservado.`,
      buttonText: 'Não',
      secondButtonText: 'Sim',
    };

    const dialogRef = this.dialog.open(DialogComponent, { data });
    dialogRef.afterClosed().subscribe(result => {
      if (!result)
        return;

      this.unsubscribe();
      this.subscription = this.httpClient
        .delete(`/api/PontoEletronicoAgendamentos/${agendamento.id}`)
        .subscribe({
          next: () => {
            this.agendamentos = this.agendamentos.filter(item => item.id !== agendamento.id);
            this.unsubscribe();
            this.snackBarService.exibirSucesso('Agendamento excluído!');
          },
          error: (err) => {
            this.unsubscribe();
            this.snackBarService.exibirErro(`Erro! - ${err.status} - ${err.statusText}`);
          }
        });
    });
  }

  private confirmarAlteracaoAtivo(agendamento: BePontoEletronicoAgendamento, ativo: boolean): void {
    if (!agendamento.id || this.subscription || agendamento.blAtivo === ativo)
      return;

    const acao = ativo ? 'ativar' : 'desativar';
    const data: DialogData = {
      title: `${ativo ? 'Ativar' : 'Desativar'} agendamento`,
      message: `Deseja ${acao} o ponto das ${agendamento.hrPonto}?`,
      buttonText: 'Não',
      secondButtonText: 'Sim',
    };

    const dialogRef = this.dialog.open(DialogComponent, { data });
    dialogRef.afterClosed().subscribe(result => {
      if (!result)
        return;

      this.unsubscribe();
      this.subscription = this.httpClient
        .put<BePontoEletronicoAgendamento>(
          `/api/PontoEletronicoAgendamentos/${agendamento.id}/ativo/${ativo}`,
          null)
        .subscribe({
          next: (res) => {
            Object.assign(agendamento, this.prepararParaTela(res));
            this.agendamentos = [...this.agendamentos];
            this.unsubscribe();
            this.snackBarService.exibirSucesso(`Agendamento ${ativo ? 'ativado' : 'desativado'}!`);
          },
          error: (err) => {
            this.unsubscribe();
            this.snackBarService.exibirErro(`Erro! - ${err.status} - ${err.statusText}`);
          }
        });
    });
  }

  private carregarAgendamentos(): void {
    this.unsubscribe();
    this.subscription = this.httpClient
      .get<BePontoEletronicoAgendamento[]>('/api/PontoEletronicoAgendamentos')
      .subscribe({
        next: (res) => {
          this.agendamentos = (res ?? []).map(item => this.prepararParaTela(item));
          this.unsubscribe();
        },
        error: (err) => {
          this.unsubscribe();
          this.snackBarService.exibirErro(`Erro! - ${err.status} - ${err.statusText}`);
        }
      });
  }

  private prepararParaTela(item: BePontoEletronicoAgendamento): BePontoEletronicoAgendamento {
    return {
      ...item,
      hrPonto: (item.hrPonto ?? '').substring(0, 5),
      dsDiasSemana: 'Segunda a sexta',
    };
  }

  private obterProximoHorarioPadrao(linhaNova: BePontoEletronicoAgendamento): string {
    const horariosCadastrados = new Set(
      this.agendamentos
        .filter(item => item !== linhaNova)
        .map(item => (item.hrPonto ?? '').substring(0, 5))
    );

    return PontoEletronicoAgendamentosComponent.HorariosPadrao
      .find(horario => !horariosCadastrados.has(horario))
      ?? PontoEletronicoAgendamentosComponent.HorariosPadrao[0];
  }

  private obterMensagemErro(erro: any, mensagemPadrao: string): string {
    if (typeof erro?.error === 'string' && erro.error.trim())
      return erro.error;

    return erro?.error?.message
      ?? erro?.error?.mensagem
      ?? mensagemPadrao;
  }

  private unsubscribe(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = undefined;
    }
  }
}
